import { Euro } from 'lucide-react';
import InputField from './InputField';
import type { WizardDaten } from '@/lib/mietwagen-wizard-validation';

interface Props {
  daten: WizardDaten;
  onChange: (updated: Partial<WizardDaten>) => void;
}

export default function Step4Konditionen({ daten, onChange }: Props) {
  const set = (field: keyof WizardDaten, value: unknown) => onChange({ [field]: value });

  const numOrNull = (v: string): number | null => {
    const n = parseFloat(v);
    return isNaN(n) ? null : n;
  };

  return (
    <div className="space-y-5 p-5">
      <div className="flex items-center gap-2 mb-1">
        <Euro className="w-4 h-4 text-orange-600" />
        <h3 className="text-sm font-semibold">Mietkonditionen</h3>
      </div>

      {/* Pflichtfelder */}
      <div>
        <h4 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Mietbeginn & Tagessatz</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <InputField required label="Mietbeginn (Datum)" value={daten.mietbeginn} onChange={v => set('mietbeginn', v)} type="date" />
          <InputField required label="Mietbeginn (Uhrzeit)" value={daten.mietbeginn_uhrzeit} onChange={v => set('mietbeginn_uhrzeit', v)} type="time" />
          <InputField label="Mietende (Datum)" value={daten.mietende} onChange={v => set('mietende', v)} type="date" />
          <InputField required label="Tagessatz (€)" value={String(daten.tagessatz)} onChange={v => set('tagessatz', parseFloat(v) || 0)} type="number" placeholder="65.00" />
        </div>
      </div>

      {/* Optionale Felder */}
      <div>
        <h4 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Optionale Konditionen</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          <InputField
            label="km-Pauschale (€)"
            value={daten.km_pauschale !== null ? String(daten.km_pauschale) : ''}
            onChange={v => set('km_pauschale', v ? numOrNull(v) : null)}
            type="number"
            placeholder="optional"
          />
          <InputField
            label="Mehr-km (€/km)"
            value={daten.mehr_km !== null ? String(daten.mehr_km) : ''}
            onChange={v => set('mehr_km', v ? numOrNull(v) : null)}
            type="number"
            placeholder="optional"
          />
          <InputField
            label="Kaution (€)"
            value={daten.kaution !== null ? String(daten.kaution) : ''}
            onChange={v => set('kaution', v ? numOrNull(v) : null)}
            type="number"
            placeholder="optional"
          />
          <InputField
            label="SB-Stufe"
            value={daten.sb_stufe}
            onChange={v => set('sb_stufe', v)}
            placeholder="z. B. Vollkasko 500 €"
          />
          <InputField
            label="Selbstbeteiligung (€)"
            value={String(daten.selbstbeteiligung)}
            onChange={v => set('selbstbeteiligung', parseFloat(v) || 0)}
            type="number"
            placeholder="0"
          />
          <InputField
            label="Versicherungspaket"
            value={daten.versicherungs_paket}
            onChange={v => set('versicherungs_paket', v)}
            placeholder="z. B. Basis / Premium"
          />
        </div>
      </div>

      {/* Gesamtberechnung */}
      {daten.mietdauer_tage > 0 && daten.tagessatz > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3">
          <p className="text-sm font-semibold text-orange-700">
            Gesamtbetrag (netto): {(daten.mietdauer_tage * daten.tagessatz).toFixed(2)} €
          </p>
          <p className="text-[11px] text-orange-500 mt-0.5">
            {daten.mietdauer_tage} Tage × {daten.tagessatz.toFixed(2)} €/Tag
          </p>
        </div>
      )}
    </div>
  );
}
